<template>
  <div>
    <button @click="triggerImageUpload">
      <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
        <path d="M16 19H2V5H11V3H2C0.9 3 0 3.9 0 5V19C0 20.1 0.9 21 2 21H16C17.1 21 18 20.1 18 19V10H16V19ZM8.21 15.83L6.25 13.47L3.5 17H14.5L10.96 12.29L8.21 15.83ZM18 3V0H16V3H13C13.01 3.01 13 5 13 5H16V7.99C16.01 8 18 7.99 18 7.99V5H21V3H18Z" fill="black"/>
      </svg>
      {{ title }}
    </button>

    <input
      v-show="false"
      ref="fileField"
      type="file"
      name="artwork-upload-button"
      label="Upload Image"
      accept="image/gif, image/jpeg, image/png, image/heic"
      @change="uploadImage($event)"
    >
  </div>
</template>

<script>
import { v4 as uuid } from "uuid";
import { add as addToDataLayer, EVENTS } from "../app/javascript/domains/DataLayer.js";
import artworkUploader from "./apihelpers/artworkUploader";
import GlazeClient from "./apihelpers/glazeClient";
import { setArtworkSizeDefaults } from "./artwork";
import { normalizedArtworkData } from "./framingUtilities.js";
import { heicToJpeg } from './heicConverter';
import { getImageErrorsForFile } from "./validateImageFile.js";

const component = {
  data () {
    return {
      activeUploads: 0,
      fileSelected: false,
      pendingUploadMethod: null
    };
  },
  props: {
    load: Function,
    checkForQuality: {
      type: Boolean,
      required: false,
      default: true
    },
    dragAndDropEvent: {
      type: Object,
      required: false,
      default: () => {
        return {};
      }
    },
    fixedArtWidth: {
      type: Number,
      required: false,
      default: null
    },
    fixedArtHeight: {
      type: Number,
      required: false,
      default: null
    },
    externalUploadUrl: {
      type: String,
      required: false,
      default: null
    },
    conveyance: {
      type: String,
      required: false,
      default: null
    },
    processingUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    joineryHost: {
      type: String,
      required: true,
      default: "/"
    },
    title: {
      type: String,
      required: false,
      default: "Upload"
    }
  },
  watch: {
    dragAndDropEvent () {
      this.uploadImage(this.dragAndDropEvent, "dragDrop");
    },
    externalUploadUrl () {
      this.uploadImage(this.externalUploadUrl, "instagram");
    }
  },
  computed: {
    GlazeClient () {
      let token;

      return new GlazeClient(token, { baseUrl: this.joineryHost });
    }
  },
  methods: {
    triggerImageUpload (e) {
      
      // If we're inside the filter form, stop prevent default that causes form submission
      // This is specifcally for the web collection filters, we may want to generalize if inside other forms again
      if (e && e.target && e.target.closest('#filterForm')) {
        e.preventDefault();
      }

      this.pendingUploadMethod = "click";
      this.$emit("processing-upload");
      this.$refs.fileField.value = null;
      this.$refs.fileField.click();
    },
    /**
     * Called when artwork is on s3, and reported to glaze, but thumbanils are not
     * yet processed.
     */
    onArtworkCreate (upload, artwork) {
      upload.artwork = { ...artwork };
      this.$emit("artwork-create", upload);
    },
    async createFileFromUrl (url) {
      const response = await fetch(url);
      const data = await response.blob();
      const metadata = {
        type: "image/jpeg"
      };
      // were only using instagram for this for now,
      // if we support uploads from elsewhere, change this name
      return new File([data], "artwork.jpg", metadata);
    },
    async convertHeicToJpeg (file) {
      // Check magic bytes — real HEIC/HEIF files have 'ftyp' at byte offset 4
      const header = await file.slice(0, 12).arrayBuffer();
      const view = new DataView(header);
      const ftyp = String.fromCharCode(view.getUint8(4), view.getUint8(5), view.getUint8(6), view.getUint8(7));
      if (ftyp !== 'ftyp') {
        // Not actually HEIC (e.g. JPEG with .HEIC extension) — use as-is
        return file;
      }

      let blob;

      try {
        blob = await heicToJpeg(file);
      } catch (error) {
        if (error?.message === "ERR_USER Image is already browser readable: image/jpeg") {
          // If the file is already a jpeg, just use it as is
          blob = file;
        } else {
          this.$emit("upload-error", "Error converting HEIC to JPEG");
          console.error("Error converting HEIC to JPEG", error);
          return null;
        }
      }

      const metadata = {
        type: "image/jpeg"
      };

      return new File([blob], "artwork.jpg", metadata);
    },
    async uploadImage (event, method) {
      this.fileSelected = true;
      let files;
      const uploadMethod = method
        || this.pendingUploadMethod
        || (this.externalUploadUrl ? "instagram" : "click");
      this.pendingUploadMethod = null;

      if (typeof event === "string") {
        const fileFromUrl = await this.createFileFromUrl(event);
        files = [fileFromUrl];
      } else {
        files = (event.target && event.target.files) || event.dataTransfer.files;
      }

      const uploadGroupUUID = uuid();

      if (!files[0]) {
        return;
      } else if (files[0].type.includes("heic")) {
        const converted = await this.convertHeicToJpeg(files[0]);
        if (!converted) return;
        files = [converted];
      }

      for (const file of files) {
        const reader = new FileReader();

        const fixedArtWidth = parseFloat(this.fixedArtWidth) || 0;
        const fixedArtHeight = parseFloat(this.fixedArtHeight) || 0;

        try {
          const value = await getImageErrorsForFile(
            file, reader, this.checkForQuality, fixedArtWidth, fixedArtHeight
          );

          this.activeUploads++;
          this.$emit("image-selected", value.img);

          const upload = {
            uuid: uuid(),
            groupId: uploadGroupUUID,
            startedAt: new Date(),
            file
          };

          this.$emit("upload-initiated", upload);

          addToDataLayer({
            event: EVENTS.UPLOAD_STARTED,
            startedAt: upload.startedAt.toISOString(),
            method: uploadMethod,
            context: "framingFlow"
          });

          const response = await artworkUploader({
            file,
            client: this.GlazeClient,
            artwork: {
              convex_metadata: value,
              use_convex: true
            },
            onUploadProgress: (event) => {
              this.$emit("upload-progress", event);
            },
            onArtworkCreate: (artwork) => {
              upload.processingStartedAt = new Date();
              addToDataLayer({
                event: EVENTS.PROCESSING_STARTED,
                startedAt: upload.processingStartedAt.toISOString(),
                context: "framingFlow"
              });

              this.onArtworkCreate(upload, normalizedArtworkData(artwork));
            }
          });

          upload.processingFinishedAt = new Date();

          addToDataLayer({
            event: EVENTS.PROCESSING_FINISHED,
            startedAt: upload.processingStartedAt.toISOString(),
            finishedAt: upload.processingFinishedAt.toISOString(),
            duration: upload.processingFinishedAt - upload.processingStartedAt,
            context: "framingFlow"
          });

          addToDataLayer({
            event: EVENTS.PHOTO_UPLOADED,
            method: this.externalUploadUrl ? "instagram" : uploadMethod,
            conveyance: this.conveyance,
            context: "framingFlow"
          });

          upload.artwork = setArtworkSizeDefaults(
            normalizedArtworkData(response.data),
            { sizeDefault: "minForSizeGroup", sizeGroup: "S" }
          );

          this.$emit("upload-complete", upload);
          
          // Track upload completion in dataLayer for analytics
          addToDataLayer({
            event: EVENTS.UPLOADED_ARTWORK
          });
          
          this.activeUploads -= 1;
        } catch (reason) {
          this.$emit("upload-error", reason);
          this.fileSelected = false;
          addToDataLayer({
            event: EVENTS.UPLOAD_ERRORED,
            reason,
            method: this.externalUploadUrl ? "instagram" : uploadMethod,
            conveyance: this.conveyance,
            context: "framingFlow"
          });
        }
      }
    }
  }
};

export default component;
</script>

<style lang="scss" scoped>
  button {
    border: 0 solid transparent;
  }

  .no-outline {
    outline: 0;
  }
</style>
